import React, { useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useActiveFixesStore } from '../hooks/useActiveFixesStore';
import { cancelFix } from '../hooks/useFixConnection';

interface Props {
  open: boolean;
  onClose: () => void;
  filePath: string;
  onSuccess?: () => void;
}

export const FixWithClaudeDialog: React.FC<Props> = ({
  open, onClose, filePath, onSuccess
}) => {
  const outputRef = useRef<HTMLDivElement>(null);

  // Subscribe to store for this file's fix status
  const fix = useActiveFixesStore((state) => state.getFix(filePath));
  const removeFix = useActiveFixesStore((state) => state.removeFix);

  // Auto-scroll on output changes
  useEffect(() => {
    if (fix?.output) {
      setTimeout(() => {
        outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
      }, 0);
    }
  }, [fix?.output]);

  // Trigger onSuccess when fix completes successfully
  useEffect(() => {
    if (fix?.status === 'success' && fix.endTime) {
      onSuccess?.();
    }
  }, [fix?.status, fix?.endTime, onSuccess]);

  const handleCancel = async () => {
    if (!fix?.processId) return;
    cancelFix(filePath, fix.processId);
    useActiveFixesStore.getState().setStatus(filePath, 'cancelled');
  };

  const handleClear = () => {
    removeFix(filePath);
    onClose();
  };

  // If no fix data, don't render
  if (!fix) {
    return null;
  }

  const isRunning = fix.status === 'running';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Fix with Claude - {filePath.split('/').pop()}
        {isRunning && (
          <Typography variant="caption" color="text.secondary" display="block">
            Running in background (dialog can be closed)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Output display */}
        <Box
          ref={outputRef}
          sx={{
            bgcolor: '#000',
            color: '#0f0',
            p: 2,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap'
          }}
        >
          {fix.output || 'Starting Claude...'}
        </Box>

        {/* Git diff (after completion) */}
        {fix.gitDiff && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>File Changes (git diff)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace'
                }}
              >
                {fix.gitDiff || 'No changes detected'}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Status message */}
        {fix.status === 'success' && (
          <Typography color="success.main" mt={2}>
            ✅ Fix completed successfully!
          </Typography>
        )}
        {fix.status === 'cancelled' && (
          <Typography color="warning.main" mt={2}>
            ⚠️ Fix cancelled
          </Typography>
        )}
        {fix.status === 'error' && (
          <Typography color="error.main" mt={2}>
            ❌ Fix failed (see output above)
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isRunning ? (
          <Button onClick={handleCancel} color="error">
            Cancel
          </Button>
        ) : (
          <>
            <Button onClick={handleClear}>Clear</Button>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
